<template>
  <div
    class="filter-chips"
    :class="{ '-vertical': layout === 'vertical' }"
  >
    <div
      v-if="advancedFilterExists"
      class="_filter-chip-row"
    >
      <m-btn
        v-if="layout === 'vertical'"
        class="_handle"
        disabled
        icon="drag"
        hide-border
        fab
        light
      />
      <filter-chip
        :ref="el => chipsRefs[advancedFilter?.key] = el"
        :filter="advancedFilter"
        :props="props"
        :disabled="disabled"
        :has-unsaved-changes="unsavedChanges.includes(advancedFilter?.key)"
        :scope-items="scopeItems"
        :account="loggedInUserAccount"
        :has-dynamic-dates="hasDynamicDates"
        :auto-open="advancedFilter.key === focusFilterChipKey"
        @update:filter="updateGroup(advancedFilter.key, $event)"
      />
    </div>
    <m-draggable
      v-if="sortedQuickFilters.length > 0"
      :draggable-item-class="layout === 'vertical' ? '_handle' : 'filter-chip'"
      :ghost-item-class="layout === 'vertical' ? '_filter-chip-row' : 'filter-chip'"
      :dragover-item-class="layout === 'vertical' ? '_filter-chip-row' : 'filter-chip'"
      :can-drag-over-right="layout === 'horizontal'"
      :can-drag-over-left="layout === 'horizontal'"
      :can-drag-over-top="layout === 'vertical'"
      :can-drag-over-bottom="layout === 'vertical'"
      scroll-container-class="_quick-filters"
      class="_quick-filters"
      :disabled="disabled || sortedQuickFilters.length < 2"
      :recreate-key="sortedQuickFilters.map(({ key }) => key)"
      :drag-between-height="30"
      :ghost-item-movement-axes="layout === 'vertical' ? 'y' : 'x'"
      @set-drag-item="setDragItem"
      @over-right="setOverRight"
      @over-left="setOverLeft"
      @over-top="setOverTop"
      @over-bottom="setOverBottom"
      @drag-drop="handleDrop"
      @cancel="cancelDragging"
    >
      <div
        v-for="(f, index) in sortedQuickFilters"
        :key="f.key"
        :data-id="f.key"
        class="_filter-chip-row"
      >
        <div
          v-if="index === 0 && dragItemId !== '' && draggingOverTop.includes(f.key)"
          class="_drag-over-top"
        />
        <m-btn
          v-if="layout === 'vertical'"
          class="_handle"
          :disabled="disabled || sortedQuickFilters.length < 2"
          icon="drag"
          hide-border
          fab
          light
        />
        <filter-chip
          :ref="el => chipsRefs[f.key] = el"
          :filter="f"
          :props="props"
          :disabled="disabled"
          :scope-items="scopeItems"
          :data-id="f.key"
          :account="loggedInUserAccount"
          :has-dynamic-dates="hasDynamicDates"
          :auto-open="f.key === focusFilterChipKey"
          :has-unsaved-changes="unsavedChanges.includes(f.key)"
          is-quick-filter
          :dragging-over-right="dragItemId !== '' && draggingOverRight.includes(f.key)"
          :dragging-over-left="index === 0 && dragItemId !== '' && draggingOverLeft.includes(f.key)"
          @update:filter="updateGroup(f.key, $event)"
          @add-to-advanced="moveQuickToAdvanced(f.key)"
        />
        <div
          v-if="dragItemId !== '' && draggingOverBottom.includes(f.key)"
          class="_drag-over-bottom"
        />
      </div>
    </m-draggable>
    <m-dropdown
      v-if="!disabled"
      v-model:value="showFilterOptions"
      :title="$t('filterChips.title')"
    >
      <m-btn
        class="_add"
        icon="plus"
        hide-border
        light
        small
      >
        <template v-if="layout === 'horizontal'">
          {{ $t('filterChips.filter') }}
        </template>
        <template v-else>
          {{ $t('filterChips.addFilter') }}
        </template>
      </m-btn>
      <template #overlay>
        <m-card no-padding>
          <filter-options
            :filter-options="unusedScopeItems"
            :advanced-filter-length="advancedFilterExists ? advancedFilter.children.length : 0"
            @add-quick-filter="addQuickFilter"
            @add-advanced-filter="addAdvancedFilter"
          />
        </m-card>
      </template>
    </m-dropdown>
  </div>
</template>

<script setup>
import FilterChip from '@/components/filter/FilterChip.vue';
import FilterOptions from '@/components/filter/FilterOptions.vue';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import useScopeFilter from '@/composables/filter/scope-filter';
import useScopeItem from '@/composables/filter/scope-item';
import useScopeItems from '@/composables/filter/scope-items';
import useSort from '@/composables/draggable/sort';
import { VIEW_EVENT_BUS } from '@/lib/constants';
import { computed, inject, onMounted, onUnmounted, ref, toRef } from 'vue';
import { copy } from 'shared/lib/copy';
import { hasValueScope, isValidTree } from '@/lib/filter/scope-tree';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { sortByArray, sortChain } from 'shared/lib/sort';

const componentProps = defineProps({
  props: {
    type: Array,
    required: true,
  },
  filter: {
    type: Object,
    default: () => null,
  },
  quickFilters: {
    type: Array,
    default: () => [],
  },
  hasDynamicDates: {
    type: Boolean,
    default: false,
  },
  unsavedChanges: {
    type: Array,
    default: () => [],
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  multiModel: {
    type: Boolean,
    default: false,
  },
  layout: {
    type: String,
    default: 'horizontal',
    validator: (value) => ['horizontal', 'vertical'].includes(value),
  },
});

const emit = defineEmits(['update:quick-filters', 'update:filter']);

const { loggedInUserAccount } = useLoggedInUserAccount();

const eventBus = inject(VIEW_EVENT_BUS);

const focusFilterChipKey = ref(null);
const chipsRefs = ref([]);

const filter = toRef(componentProps, 'filter');
const quickFilters = toRef(componentProps, 'quickFilters');

const { scopeItems, scopeItemByProp } = useScopeItems(
  computed(() => componentProps.props.filter((p) => !p.property.noFilter)),
  false,
  componentProps.multiModel,
);
const scopeFilterSvc = useScopeFilter(scopeItems, filter, quickFilters);
const {
  unusedScopeItems,
  advancedFilter,
  advancedFilterExists,
} = scopeFilterSvc;

const { selectedScopeItem } = useScopeItem();
const filterSorter = (filterOrder) => sortChain([
  (a, b) => {
    const aHasValue = hasValueScope(a.children[0].children[0].scope);
    const bHasValue = hasValueScope(b.children[0].children[0].scope);
    if ((aHasValue && bHasValue) || (!aHasValue && !bHasValue)) {
      return 0;
    }
    return aHasValue ? -1 : 1;
  },
  sortByArray(filterOrder.map((key) => ({ key })), 'key')]);

const sortedQuickFilters = computed(() => [...filter.value.children]
  .filter((filter) => componentProps.quickFilters.includes(filter.key))
  .sort(filterSorter(componentProps.quickFilters)));

const isUsedScopeItem = (scopeItem) => scopeFilterSvc.usedScopeItems.value.find((p) => p.value === scopeItem.value) !== undefined;

const addQuickFilterFromProp = (prop = null) => {
  if (prop == null) {
    return;
  }
  const scopeItem = scopeItemByProp(prop);
  addQuickFilter(scopeItem);
};

const showFilterOptions = ref(false);
const addQuickFilter = (scopeItem) => {
  if (isNullOrUndefined(scopeItem)) {
    return;
  }
  showFilterOptions.value = false;
  if (isUsedScopeItem(scopeItem)) {
    const existingQuickFilter = sortedQuickFilters.value.find((filter) => {
      const filterScope = filter.children[0].children[0].scope;
      return selectedScopeItem(scopeItems.value, filterScope).value === scopeItem.value;
    });
    if (existingQuickFilter !== undefined) {
      const childRef = chipsRefs.value[existingQuickFilter.key];
      childRef?.show();
    }
    return;
  }
  const node = scopeFilterSvc.buildNode(scopeItem);
  const newGroup = createGroup(node);
  updateQuickFilters([...componentProps.quickFilters, newGroup.key]);
};

const firstUnusedScopeItem = computed(() => {
  if (scopeFilterSvc.unusedScopeItems.value.length === 0) {
    return null;
  }
  return scopeFilterSvc.unusedScopeItems.value[0];
});

const addAdvancedFilter = () => {
  showFilterOptions.value = false;
  if (scopeFilterSvc.advancedFilterExists.value) {
    const childRef = chipsRefs.value[scopeFilterSvc.advancedFilter.value.key];
    childRef?.show();
    return;
  }

  if (firstUnusedScopeItem.value === null) {
    return;
  }

  const node = scopeFilterSvc.buildNode(firstUnusedScopeItem.value);
  createGroup(node);
};

const moveQuickToAdvanced = (key) => {
  if (advancedFilterExists.value) {
    const userScopeTree = scopeFilterSvc.buildMoveNodeToGroupByKey(key, advancedFilter.value.key);
    update(userScopeTree);
  }
  updateQuickFilters(componentProps.quickFilters.filter((qKey) => qKey !== key));
};

const createGroup = (node) => {
  if (node === undefined) {
    return undefined;
  }
  const { filter, newGroup } = scopeFilterSvc.buildFilterWithNewNode(node);
  focusFilterChipKey.value = newGroup.key;
  update(filter);
  return newGroup;
};

const updateGroup = (key, value) => {
  if (value === undefined) {
    return;
  }
  const filterIndex = filter.value.children.findIndex((node) => node.key === key);
  if (filterIndex === -1) {
    return;
  }

  const userScopeTree = copy(filter.value);
  if (value !== null) {
    userScopeTree.children[filterIndex] = value;
  } else {
    updateQuickFilters(componentProps.quickFilters.filter((k) => k !== key));
    userScopeTree.children.splice(filterIndex, 1);
  }
  update(userScopeTree);
};

const update = (value) => {
  if (!isValidTree(value, { quickFilters: componentProps.quickFilters })) {
    return;
  }
  emit('update:filter', value);
};

const updateQuickFilters = (value) => {
  emit('update:quick-filters', value);
};

const {
  setDragItem,
  setOverRight,
  setOverLeft,
  setOverTop,
  setOverBottom,
  draggingOverRight,
  draggingOverLeft,
  draggingOverTop,
  draggingOverBottom,
  dropItem,
  cancelDragging,
  dragItemId,
} = useSort('key');

const handleDrop = () => {
  const newOrder = dropItem(sortedQuickFilters.value);
  // We need to sort to ensure filters with values come first
  newOrder.sort(filterSorter(newOrder));
  updateQuickFilters(newOrder.map(({ key }) => key));
};

onMounted(() => {
  eventBus.$on('applyFilter', addQuickFilterFromProp);
  eventBus.$on('applyAdvancedFilter', addAdvancedFilter);
});

onUnmounted(() => {
  eventBus.$off('applyFilter', addQuickFilterFromProp);
  eventBus.$off('applyAdvancedFilter', addAdvancedFilter);
});
</script>

<style scoped lang="scss" type="text/scss">
.filter-chips {
  display: flex;
  gap: .8rem;

  ._quick-filters {
    display: flex;
    gap: .8rem;
  }

  &.-vertical {
    flex-direction: column;
    gap: .2rem;

    ._quick-filters {
      flex-direction: column;
      gap: .2rem;
    }
  }

}

._overlay {
  min-width: 35rem;
  max-width: 90rem;

  ._top {
    display: flex;

    ._select {
      margin-left: auto;
    }
  }
}

._filter-chip-row {
  display: flex;
  align-items: center;
  gap: .8rem;
  position: relative;

  ._handle {
    width: 2rem;
    cursor: grab;
  }

  ._drag-over-top {
    position: absolute;
    top: -4px;
    left: 0;
    z-index: 88;
    width: 100%;
    height: 4px;
    pointer-events: none;
    background: $highlighted-color-dark;
    opacity: 1;
  }

  ._drag-over-bottom {
    position: absolute;
    bottom: -4px;
    left: 0;
    z-index: 88;
    width: 100%;
    height: 4px;
    pointer-events: none;
    background: $highlighted-color-dark;
    opacity: 1;
  }
}
</style>
